function defaultNormalizeLink(href) {
    return href;
}
export function parsePublicMarkdownInline(value, options = {}) {
    const normalizeLink = options.normalizeLink ?? defaultNormalizeLink;
    const nodes = [];
    let index = 0;
    while (index < value.length) {
        const strongIndex = value.indexOf("**", index);
        const codeIndex = value.indexOf("`", index);
        const linkIndex = value.indexOf("[", index);
        const candidates = [strongIndex, codeIndex, linkIndex].filter((candidate) => candidate >= 0);
        const nextIndex = candidates.length ? Math.min(...candidates) : -1;
        if (nextIndex < 0) {
            nodes.push({ type: "text", value: value.slice(index) });
            break;
        }
        if (nextIndex > index)
            nodes.push({ type: "text", value: value.slice(index, nextIndex) });
        if (nextIndex === strongIndex) {
            const end = value.indexOf("**", nextIndex + 2);
            if (end >= 0) {
                nodes.push({
                    type: "strong",
                    value: parsePublicMarkdownInline(value.slice(nextIndex + 2, end), options),
                });
                index = end + 2;
                continue;
            }
        }
        if (nextIndex === codeIndex) {
            const end = value.indexOf("`", nextIndex + 1);
            if (end >= 0) {
                nodes.push({ type: "code", value: value.slice(nextIndex + 1, end) });
                index = end + 1;
                continue;
            }
        }
        if (nextIndex === linkIndex) {
            const labelEnd = value.indexOf("]", nextIndex + 1);
            const hrefStart = labelEnd >= 0 ? value.indexOf("(", labelEnd) : -1;
            const hrefEnd = hrefStart >= 0 ? value.indexOf(")", hrefStart) : -1;
            if (labelEnd >= 0 && hrefStart === labelEnd + 1 && hrefEnd >= 0) {
                nodes.push({
                    type: "link",
                    label: parsePublicMarkdownInline(value.slice(nextIndex + 1, labelEnd), options),
                    href: normalizeLink(value.slice(hrefStart + 1, hrefEnd)),
                });
                index = hrefEnd + 1;
                continue;
            }
        }
        nodes.push({ type: "text", value: value[nextIndex] ?? "" });
        index = nextIndex + 1;
    }
    return nodes.filter((node) => node.type !== "text" || node.value.length > 0);
}
function splitTableRow(line) {
    return line
        .replace(/^\|/, "")
        .replace(/\|$/, "")
        .split("|")
        .map((cell) => cell.trim());
}
function isTableSeparator(line) {
    return /^\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?$/.test(line);
}
function isHorizontalRule(line) {
    return /^(?:-{3,}|_{3,}|\*{3,})$/.test(line.trim().replace(/[\t ]/g, ""));
}
export function parsePublicMarkdown(markdown, options = {}) {
    const lines = markdown.replace(/\r\n/g, "\n").split("\n");
    const blocks = [];
    let index = 0;
    while (index < lines.length) {
        const line = lines[index] ?? "";
        if (!line.trim()) {
            index += 1;
            continue;
        }
        const codeStart = line.match(/^```(.*)$/);
        if (codeStart) {
            const language = codeStart[1]?.trim() ?? "";
            const codeLines = [];
            index += 1;
            while (index < lines.length && !(lines[index] ?? "").startsWith("```")) {
                codeLines.push(lines[index] ?? "");
                index += 1;
            }
            blocks.push({ type: "code", language, code: codeLines.join("\n") });
            index += 1;
            continue;
        }
        if (isHorizontalRule(line)) {
            blocks.push({ type: "horizontal-rule" });
            index += 1;
            continue;
        }
        const heading = line.match(/^(#{1,6})\s+(.+)$/);
        if (heading?.[1] && heading[2]) {
            blocks.push({
                type: "heading",
                level: heading[1].length,
                content: parsePublicMarkdownInline(heading[2].trim(), options),
            });
            index += 1;
            continue;
        }
        if (line.trim().startsWith("|") && lines[index + 1] && isTableSeparator((lines[index + 1] ?? "").trim())) {
            const headers = splitTableRow(line).map((cell) => parsePublicMarkdownInline(cell, options));
            const rows = [];
            index += 2;
            while (index < lines.length && (lines[index] ?? "").trim().startsWith("|")) {
                rows.push(splitTableRow(lines[index] ?? "").map((cell) => parsePublicMarkdownInline(cell, options)));
                index += 1;
            }
            blocks.push({ type: "table", headers, rows });
            continue;
        }
        if (/^\s*-\s+/.test(line)) {
            const items = [];
            while (index < lines.length && /^\s*-\s+/.test(lines[index] ?? "")) {
                items.push(parsePublicMarkdownInline((lines[index] ?? "").replace(/^\s*-\s+/, ""), options));
                index += 1;
            }
            blocks.push({ type: "list", items });
            continue;
        }
        const paragraphLines = [];
        while (index < lines.length
            && (lines[index] ?? "").trim()
            && !/^(#{1,6})\s+/.test(lines[index] ?? "")
            && !/^```/.test(lines[index] ?? "")
            && !isHorizontalRule(lines[index] ?? "")
            && !/^\s*-\s+/.test(lines[index] ?? "")
            && !((lines[index] ?? "").trim().startsWith("|") && lines[index + 1] && isTableSeparator((lines[index + 1] ?? "").trim()))) {
            paragraphLines.push((lines[index] ?? "").trim());
            index += 1;
        }
        blocks.push({
            type: "paragraph",
            content: parsePublicMarkdownInline(paragraphLines.join(" "), options),
        });
    }
    return blocks;
}
//# sourceMappingURL=index.js.map